<script>
  import { asText } from '@prismicio/client'
  import { page } from '$app/stores'

  import { text, register } from '$lib/text.js'
  import { setMeta } from '$lib/Meta.svelte'
  import { resolve } from '$lib/prismic.js'
  import PageHeading from '$lib/PageHeading.svelte'
  import Header from '$lib/Header.svelte'
  import Footer from '$lib/Footer.svelte'
  import Button from '$lib/Button.svelte'
  import Image from '$lib/Image.svelte'
  import { i18n } from './i18n.js'

  register(i18n)

  export let data

  let groups = data.routes.reduce((acc, item) => {
    const key = item.data.origin.id
    const group = acc[key]
    if (group) {
      group.items.push(item)
    } else {
      acc[key] = { items: [item], expanded: $page.url.searchParams.has(key) }
    }
    return acc
  }, {})

  $: setMeta({
    title: $text`Recommended routes`
  })
</script>

<Header
  languages={[
    { href: '/routes', lang: 'en-gb' },
    { href: '/de/routes', lang: 'de-de' },
    { href: '/fr/routes', lang: 'fr-fr' },
    { href: '/es/routes', lang: 'es-es' },
    { href: '/sv/routes', lang: 'sv-se' }
  ]} />

<div class="page u-container">
  <PageHeading title={$text`Recommended routes`}>
    <svelte:fragment slot="body">
      <p>
        {$text`Get inspiration, explore expert routes and easily book train tickets to your next sustainable European adventure.`}
      </p>
    </svelte:fragment>
  </PageHeading>

  <div class="groups">
    {#each Object.entries(groups) as [key, { items: [first, ...items], expanded }], index}
      {@const src = first.data.variants[0].primary.image.url}
      {@const origin =
        data.translations.find(
          (doc) =>
            doc.data.location.uid === first.data.origin?.data.location.uid
        ) || first.data.origin}
      {@const destination =
        data.translations.find(
          (doc) =>
            doc.data.location.uid === first.data.destination?.data.location.uid
        ) || first.data.destination}
      {@const originName = asText(origin.data.name)}
      {@const destinationName = asText(destination.data.name)}
      <div class="group">
        <a class="card" href={resolve(first)}>
          <Image
            {src}
            {...first.data.variants[0].primary.image.dimensions}
            class="image"
            alt={asText(first.data.name) || ''}
            loading="lazy"
            variants={[300, 600, [900, 'q_60']]} />
          <span class="label">{$text`Popular`}</span>
          <div class="about">
            <h3 class="title">
              {#if index % 4 === 2}
                {$text`Trains from ${originName} to ${destinationName}`}
              {:else if index % 4 === 1}
                {$text`Travel from ${originName} to ${destinationName}`}
              {:else}
                {$text`${originName} to ${destinationName} by train`}
              {/if}
            </h3>
            <span>
              {#if index % 3 === 2}
                {$text`Learn how to best travel from ${originName} to ${destinationName} by train.`}
              {:else if index % 3 === 1}
                {$text`Get the best train route to ${destinationName} from ${originName}.`}
              {:else}
                {$text`Go from ${originName} to ${destinationName} in ${first.data.variants[0].items.length} simple steps.`}
              {/if}
            </span>
            <span class="target">{$text`See route`}</span>
          </div>
        </a>
        <ol class="items">
          {#each items.slice(0, expanded ? items.length : 3) as doc, _index}
            {@const originName = `<strong>${asText(doc.data.origin.data.name)}</strong>`}
            {@const destinationName = `<strong>${asText(
              doc.data.destination.data.name
            )}</strong>`}
            <li class="item">
              <a href={resolve(doc)} class="u-link">
                {#if (_index + index) % 4 === 3}
                  {@html $text`${originName} to ${destinationName} by train`}
                {:else if (_index + index) % 4 === 2}
                  {@html $text`Trains from ${originName} to ${destinationName}`}
                {:else if (_index + index) % 4 === 1}
                  {@html $text`${originName} to ${destinationName}`}
                {:else}
                  {@html $text`Travel from ${originName} to ${destinationName}`}
                {/if}
              </a>
            </li>
          {/each}
        </ol>
        {#if !expanded && items.length > 3}
          <Button
            muted
            size="sm"
            href="?{key}"
            onclick={(event) => {
              event.preventDefault()
              groups[key].expanded = true
            }}>
            {$text`Show more`}
          </Button>
        {/if}
      </div>
    {/each}
  </div>
</div>

<Footer />

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 4rem;
    padding: 5rem 0;
  }

  @media (min-width: 1000px) {
    .page {
      padding: 8rem 0;
      gap: 6rem;
    }
  }

  .groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 3.5rem 2.5rem;
    width: 100%;
  }

  .group {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  /* Card */

  .card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    aspect-ratio: 0.8;
    padding: 1.5rem;
    position: relative;
    z-index: 0;
    overflow: hidden;
    color: rgb(var(--rgb-white));
    border-radius: var(--border-radius-lg);
  }

  .card::before {
    content: '';
    display: block;
    width: calc(100% + 2rem);
    height: calc(100% + 2rem);
    margin: -1rem 0 0 -1rem;
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.2) 50%,
      rgba(0, 0, 0, 0.8) 100%
    );
  }

  .card :global(.image) {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: -2;
    object-fit: cover;
  }

  .label {
    font-size: 1rem;
    font-weight: 600;
  }

  .about {
    display: grid;
    gap: 0.75rem;
    text-wrap: balance;
    transform: translateY(2.15rem);
    transition: transform 250ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  .card:hover .about {
    transform: translateY(0rem);
  }

  .title {
    font-family: var(--heading-font-family);
    font-weight: bold;
    font-size: 1.5rem;
    line-height: 1.15;
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
    text-wrap: balance;
    max-width: 70%;
  }

  @media (min-width: 900px) {
    .title {
      font-size: 1.6rem;
      line-height: 1.1;
    }
  }

  .target {
    color: currentColor;
    font-weight: 600;
    text-decoration: none;
    opacity: 0;
    transition: opacity 250ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  /* Items */

  .items {
    --rgb-link: var(--rgb-dark-gray);

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    font-size: 1rem;
  }
</style>
